import React, { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../api_client";
import { Table, Checkbox, message, Card, Row, Col, Input, Tooltip } from "antd";
import { API_URL } from "../../url_config";
import {NavigationStepsKeolis} from "../../components/navbars/navbar_keolis"
import { Button } from "antd";
import { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";
import CoquillesTable from "../../components/counter";
const { TextArea } = Input;

// === Style du tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }
`;
document.head.appendChild(tableStyle);

// === Composant interne (zones texte : Commentaire + Aide) ===
function TablePoSiel({ record, onChange }) {
  const [comment, setComment] = useState(record.comment || "");
  const [aide, setAide] = useState(record.aide || "");

  useEffect(() => {
    setComment(record.comment || "");
    setAide(record.aide || "");
  }, [record.comment, record.aide]);

  const handleChange = (field, value) => {
    onChange(record.key, field, value);
  };

  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <TextArea
              rows={4}
              value={comment}
              onChange={(e) => {
                const value = e.target.value;
                setComment(value);
                handleChange("comment", value);
              }}
              placeholder="Saisis un commentaire..."
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Informations</span>
              <span style={{ fontSize: 12, color: "#888" }}>
                (Supervision)
              </span>
              </div>
            }
            bordered
          >
            <TextArea
              rows={4}
              value={aide}
              onChange={(e) => {
                const value = e.target.value;
                setAide(value);
                handleChange("aide", value);
              }}
              placeholder="Décris ton besoin d’aide..."
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const ValidationPoSielKeolis = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const isMountedRef = useRef(true);
  const pendingUpdatesRef = useRef(new Map());
  const timeoutRefs = useRef(new Map());

  // Fonction pour charger les données
  const fetchData = useCallback(async () => {
    try {
      const res = await apiClient.get(
        `${API_URL}/validations/validation_po_siel/?ot_tag=keolis`
      );
      if (!isMountedRef.current) return;

        const formatted = res.data.map((item, index) => ({
          key: index,
          id: item.id,
          ligne: item.ligne,
          presence_ligne_sur_po: item.presence_ligne_sur_po,
          chargement_tm: item.chargement_tm,
          presence_synoptique: item.presence_synoptique,
          message_iv_saisie: item.message_iv_saisie,
          prise_service_geoloc_sae: item.prise_service_geoloc_sae,
          iv_indispo: item.iv_indispo,
          comment: item.comment,
          aide: item.aide,
          signal: item.signal,
        }));

        setData(formatted);
      } catch (error) {
        console.error("Erreur de chargement :", error);
        message.error("Erreur de chargement des données");
      }
  }, []);

  // Rafraîchissement manuel avec loading
  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const sendUpdate = async (updateKey) => {
    const update = pendingUpdatesRef.current.get(updateKey);
    if (!update) return;

    try {
      await apiClient.patch(
        `${API_URL}/validations/validation_po_siel/${update.id}/`,
        { [update.field]: update.value }
      );
      const current = pendingUpdatesRef.current.get(updateKey);
      if (current && current.timestamp === update.timestamp) {
        pendingUpdatesRef.current.delete(updateKey);
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur de mise à jour");
    }
  };

  // Charger les données au montage uniquement
  useEffect(() => {
    isMountedRef.current = true;
    setLoading(true);
    fetchData().finally(() => setLoading(false));

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData]);

  const handleDataChange = (key, field, value) => {
    let itemId;
    setData((prev) => {
      const updated = prev.map((item) => {
        if (item.key === key) {
          itemId = item.id;
          return { ...item, [field]: value };
        }
        return item;
      });
      return updated;
    });

    if (itemId) {
      const updateKey = `${itemId}_${field}`;
      
      const existingTimeout = timeoutRefs.current.get(updateKey);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      
      pendingUpdatesRef.current.set(updateKey, { 
        id: itemId, 
        field, 
        value,
        timestamp: Date.now()
      });
      
      const timeoutId = setTimeout(() => {
        sendUpdate(updateKey);
        timeoutRefs.current.delete(updateKey);
      }, 1000);
      
      timeoutRefs.current.set(updateKey, timeoutId);
    }
  };

  // Filtrer les données selon la recherche
  const filteredData = data.filter((item) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      (item.ligne?.toString().toLowerCase() || "").includes(search) ||
      (item.comment?.toString().toLowerCase() || "").includes(search) ||
      (item.aide?.toString().toLowerCase() || "").includes(search)
    );
  });

  const columns = [
    {
      title: "Ligne",
      dataIndex: "ligne",
      key: "ligne",
      sorter: (a, b) => (a.ligne || "").localeCompare(b.ligne || ""),
      render: (val, record) => (
        <Input
          value={val}
          onChange={(e) =>
            handleDataChange(record.key, "ligne", e.target.value)
          }
          placeholder="Ligne"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Présence ligne sur PO",
      dataIndex: "presence_ligne_sur_po",
      key: "presence_ligne_sur_po",
      sorter: (a, b) => (a.presence_ligne_sur_po === b.presence_ligne_sur_po ? 0 : a.presence_ligne_sur_po ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "presence_ligne_sur_po", e.target.checked)
          }
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Chargement TM",
      dataIndex: "chargement_tm",
      key: "chargement_tm",
      sorter: (a, b) => (a.chargement_tm === b.chargement_tm ? 0 : a.chargement_tm ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "chargement_tm", e.target.checked)
          }
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Présence synoptique",
      dataIndex: "presence_synoptique",
      key: "presence_synoptique",
      sorter: (a, b) => (a.presence_synoptique === b.presence_synoptique ? 0 : a.presence_synoptique ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "presence_synoptique", e.target.checked)
          }
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Prise de service / géoloc SAE",
      dataIndex: "prise_service_geoloc_sae",
      key: "prise_service_geoloc_sae",
      sorter: (a, b) => (a.prise_service_geoloc_sae === b.prise_service_geoloc_sae ? 0 : a.prise_service_geoloc_sae ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "prise_service_geoloc_sae", e.target.checked)
          }
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "IV Indispo",
      dataIndex: "iv_indispo",
      key: "iv_indispo",
      sorter: (a, b) => (a.iv_indispo === b.iv_indispo ? 0 : a.iv_indispo ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "iv_indispo", e.target.checked)
          }
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    /*
    {
      title: "Message IV saisi",
      dataIndex: "message_iv_saisie",
      key: "message_iv_saisie",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "message_iv_saisie", e.target.checked)
          }
        />
      ),
    },
    */
    {
  title: "ETAT",
  dataIndex: "signal",
  sorter: (a, b) => a.signal - b.signal,
  render: (val, record) => {
    // 0 = pas encore choisi, 1 = validé, -1 = non validé
    const color =
      val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
    const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";

    const handleClick = () => {
      // Cycle entre 0 → 1 → -1 → 0 ...
      const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
      handleDataChange(record.key, "signal", nextVal);
    };

    return (
      <div
        onClick={handleClick}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            backgroundColor: color,
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "16px",
            userSelect: "none",
            transition: "all 0.3s ease",
          }}
        >
          {symbol}
        </div>
      </div>
    );
  },
},
  ];

  return (
    <div style={{ padding: "20px" }}>
    <NavigationStepsKeolis selectedKey="po_siel" />
      
      <CoquillesTable 
        url={`${API_URL}/validations/counter/po_siel/?ot_tag=keolis`} 
        labelAll="Nb lignes DSP"
        labelOk="Nb lignes OK"
        labelNok="Nb lignes KO"
        labelExtra="Nb lignes avec IV indispo"
        showExtra={true}
      />

      <Input
        placeholder="Rechercher dans toutes les colonnes..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ 
          marginBottom: "16px", 
          width: "100%",
          maxWidth: "500px"
        }}
        allowClear
      />

      <Tooltip title="Rafraîchir les données">
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<ReloadOutlined style={{ fontSize: "20px" }} />}
          onClick={handleManualRefresh}
          loading={refreshing}
          className="app-refresh-button"
          style={{
            zIndex: 9999,
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15)",
            background: "linear-gradient(135deg, #40a9ff 0%, #1890ff 100%)",
            border: "none",
            width: "56px",
            height: "56px"
          }}
        />
      </Tooltip>

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <TablePoSiel
              record={record}
              onChange={handleDataChange}
            />
          ),
        }}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
        rowClassName={() => "custom-row"}
pagination={false}
      />

      <div style={{ padding: 20 }}>

            <ModifyItemPage
              apiUrl={`${API_URL}/validations/validation_po_siel/`}
              ot_tag="keolis"
              title="Gestion"
              rowKey="id"
              trigger={<Button icon={<EditOutlined />} shape="circle" />}
              columns={[
                { title: "Ligne", dataIndex: "ligne", key: "ligne" },

              ]}
              formFields={[
                { name: "ligne", label: "Ligne", placeholder: "Entrez la Ligne" },
              ]}
              onRefresh={fetchData}
            />
      </div>


    </div>
  );
};

export default ValidationPoSielKeolis;
